import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Select, MenuItem, FormControl, InputLabel,
  FormControlLabel, Checkbox, Box, Alert, Typography,
} from '@mui/material';
import type { DepartmentRuleView } from '../../../../services/LaborManagementService/types/laborManagementTypes';

// ── Formula Type options ───────────────────────────────────────────

const FORMULA_TYPES = [
  { value: 'CaseBased', label: 'Case Based' },
  { value: 'CaseDurationBased', label: 'Case Duration Based' },
  { value: 'RoomBased', label: 'Room Based' },
  { value: 'TrayBased', label: 'Tray Based' },
  { value: 'Manual', label: 'Manual' },
];

const DEPARTMENT_NAMES = [
  { value: 'PreOpRN', label: 'Pre-Op RN' },
  { value: 'PacuRN', label: 'PACU RN' },
  { value: 'ORCirculatingRN', label: 'OR Circulating RN' },
  { value: 'ORScrubTech', label: 'OR Scrub Tech' },
  { value: 'XrayTech', label: 'X-ray Tech' },
  { value: 'FirstAssist', label: 'First Assist' },
  { value: 'SterileProcessingTech', label: 'Sterile Processing Tech' },
  { value: 'PatientCareTech', label: 'Patient Care Tech' },
  { value: 'ORRunnerFloat', label: 'OR Runner/Float' },
];

// ── Factor drafts ──────────────────────────────────────────────────

// Legal *while typing*: the empty string, a bare '.', a trailing '0.'. Three fraction
// digits, because seeded rules already store a third place (Pre-Op RN 0.286).
const FACTOR_DRAFT = /^\d*\.?\d{0,3}$/;

/** Returns the message to show, or null when the draft is a usable number. */
const factorError = (label: string, draft: string, min: number, max: number): string | null => {
  const value = parseFloat(draft);
  if (draft.trim() === '' || Number.isNaN(value)) return `Please enter a ${label}.`;
  if (value < min) return `${label} cannot be below ${min}.`;
  if (value > max) return `${label} cannot be above ${max}.`;
  if (!FACTOR_DRAFT.test(draft)) return `${label} cannot have more than three decimal places.`;
  return null;
};

/** Text field holding a decimal draft. A refused keystroke leaves the draft as it was. */
const FactorField: React.FC<{
  label: string;
  value: string;
  onChange: (draft: string) => void;
  helperText?: string;
}> = ({ label, value, onChange, helperText }) => (
  <TextField
    label={label}
    size="small"
    fullWidth
    inputProps={{ inputMode: 'decimal' }}
    value={value}
    onChange={e => { if (FACTOR_DRAFT.test(e.target.value)) onChange(e.target.value); }}
    helperText={helperText}
  />
);

/**
 * Two-row note box. It carries its own InputProps.style because the theme sets a fixed
 * 40px height inline on every text field, and an inline style beats any `sx` class, so
 * the note's text overflows up into the floating label's notch. fontSize is restated
 * because MUI replaces the theme's whole InputProps object rather than merging into it.
 */
const NotesField: React.FC<{
  value: string;
  onChange: (notes: string) => void;
}> = ({ value, onChange }) => (
  <TextField
    label="Notes"
    size="small"
    fullWidth
    multiline
    rows={2}
    value={value}
    onChange={e => onChange(e.target.value)}
    InputProps={{ style: { height: 'auto', fontSize: 16 } }}
  />
);

/**
 * A grid row click opens this dialog straight under the pointer, so the second click of a
 * double-click on the row lands on whatever control the dialog has just drawn there. Where the row
 * and the Enabled box line up, that click clears the box, and the save then writes a department off
 * with nothing on screen to say so. A click carrying `detail` above 1 belongs to a gesture that
 * began before the dialog existed, so it is never a choice made in the dialog.
 */
const ignoreTheClickThatOpenedTheDialog = (event: React.MouseEvent) => {
  if (event.detail <= 1) return;
  event.preventDefault();
  event.stopPropagation();
};

// ── Component ──────────────────────────────────────────────────────

interface DepartmentRuleDialogProps {
  open: boolean;
  rule: DepartmentRuleView | null; // null = add mode, value = edit mode
  existingDepartments: string[]; // departments already in use (for add validation)
  onClose: () => void;
  onSave: (rule: DepartmentRuleView) => void;
}

const DepartmentRuleDialog: React.FC<DepartmentRuleDialogProps> = ({
  open, rule, existingDepartments, onClose, onSave,
}) => {
  const isEditing = rule !== null;

  const [departmentName, setDepartmentName] = useState('');
  const [formulaType, setFormulaType] = useState('CaseBased');
  const [scalingFactorDraft, setScalingFactorDraft] = useState('1');
  const [utilizationFactorDraft, setUtilizationFactorDraft] = useState('1');
  const [enabled, setEnabled] = useState(true);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Reset form when dialog opens or rule changes
  useEffect(() => {
    if (open) {
      if (rule) {
        setDepartmentName(rule.departmentName);
        setFormulaType(rule.formulaType);
        setScalingFactorDraft(String(rule.scalingFactor));
        setUtilizationFactorDraft(String(rule.utilizationFactor ?? 1));
        setEnabled(rule.enabled);
        setNotes(rule.notes || '');
      } else {
        setDepartmentName('');
        setFormulaType('CaseBased');
        setScalingFactorDraft('1');
        setUtilizationFactorDraft('1');
        setEnabled(true);
        setNotes('');
      }
      setError(null);
    }
  }, [open, rule]);

  // Filter available departments for add mode
  const availableDepartments = isEditing
    ? DEPARTMENT_NAMES
    : DEPARTMENT_NAMES.filter(d => !existingDepartments.includes(d.value));

  const handleSave = () => {
    if (!departmentName) {
      setError('Please select a department.');
      return;
    }

    const factorProblem =
      factorError('Scaling Factor', scalingFactorDraft, 0, Number.POSITIVE_INFINITY)
      ?? factorError('Utilization Factor', utilizationFactorDraft, 0.01, 1);
    if (factorProblem) {
      setError(factorProblem);
      return;
    }

    const saved: DepartmentRuleView = {
      id: rule?.id || crypto.randomUUID(),
      departmentName,
      formulaType,
      scalingFactor: parseFloat(scalingFactorDraft),
      utilizationFactor: parseFloat(utilizationFactorDraft),
      enabled,
      notes,
    };
    onSave(saved);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth onClickCapture={ignoreTheClickThatOpenedTheDialog}>
      <DialogTitle sx={{ fontWeight: 600 }}>
        {isEditing ? 'Edit Department Rule' : 'Add Department Rule'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <FormControl fullWidth size="small" disabled={isEditing}>
            <InputLabel>Department</InputLabel>
            <Select
              value={departmentName}
              label="Department"
              onChange={e => setDepartmentName(e.target.value)}
            >
              {availableDepartments.map(d => (
                <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>Formula Type</InputLabel>
            <Select
              value={formulaType}
              label="Formula Type"
              onChange={e => setFormulaType(e.target.value)}
            >
              {FORMULA_TYPES.map(f => (
                <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FactorField
            label="Scaling Factor"
            value={scalingFactorDraft}
            onChange={setScalingFactorDraft}
          />

          <FactorField
            label="Utilization Factor"
            value={utilizationFactorDraft}
            onChange={setUtilizationFactorDraft}
            helperText="Occupancy ratio (0.01–1.0). Lower values reduce staffing."
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={enabled}
                onChange={e => setEnabled(e.target.checked)}
              />
            }
            label="Enabled"
          />

          <NotesField value={notes} onChange={setNotes} />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
        <Typography variant="caption" sx={{ color: '#64748b' }}>
          Rules are not deleted. Turn a rule off by clearing its Enabled box.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            sx={{
              backgroundColor: '#3b82f6',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { backgroundColor: '#2563eb' },
            }}
          >
            {isEditing ? 'Save' : 'Add'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default DepartmentRuleDialog;
